import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useCurrency } from '../../../contexts/CurrencyContext';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 24px 12px 32px;
  }
`;

const Header = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h1 {
    font-size: 1.5rem;
    color: #333;
    margin: 0;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;

  .title {
    h2 {
      font-size: 1.25rem;
      color: #333;
      margin: 0 0 4px 0;
    }

    .subtitle {
      font-size: 0.875rem;
      color: #666;
    }
  }

  .status {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    
    &.active {
      background: #e6f4ea;
      color: #1e7e34;
    }
    
    &.pending {
      background: #fff3e0;
      color: #f57c00;
    }
    
    &.expired {
      background: #ffebee;
      color: #c62828;
    }

    &.cancelled {
      background: #f5f5f5;
      color: #757575;
    }
  }
`;

const PlanDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;

  .detail {
    .label {
      font-size: 0.75rem;
      color: #666;
      margin-bottom: 4px;
    }

    .value {
      font-size: 0.875rem;
      color: #333;
      font-weight: 500;
    }
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PlanActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &.primary {
    background: #34574A;
    color: white;
    border: none;

    &:hover {
      background: #2a4539;
    }
  }

  &.secondary {
    background: white;
    border: 1px solid #eee;
    color: #666;

    &:hover {
      background: #f8f9fa;
    }
  }
`;

const PaymentsSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-top: 8px;

  @media (max-width: 768px) {
    border-radius: 16px;
    margin-top: 16px;
  }
`;

const SectionHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.25rem;
    color: #333;
    margin: 0;
  }
`;

const FiltersContainer = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 16px;
  border: 2px solid #eee;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;

  &:focus {
    border-color: #34574A;
    outline: none;
  }
`;

const PaymentsTable = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px 18px;
    text-align: left;
    font-size: 0.875rem;
    white-space: nowrap;
  }

  th {
    background: #f8f9fa;
    color: #666;
    font-weight: 500;
  }

  td {
    color: #333;
    border-bottom: 1px solid #eee;
  }

  /* permite que a descrição quebre em vez de forçar scroll horizontal demais */
  td:nth-child(3) {
    white-space: normal;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const PaymentStatus = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  
  &.paid {
    background: #e6f4ea;
    color: #1e7e34;
  }
  
  &.pending {
    background: #fff3e0;
    color: #f57c00;
  }
  
  &.expired {
    background: #ffebee;
    color: #c62828;
  }
  
  &.cancelled {
    background: #f5f5f5;
    color: #757575;
  }
`;

const Payments = () => {
  const [periodFilter, setPeriodFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [activePlans, setActivePlans] = useState([]);
  const [activePlansLoading, setActivePlansLoading] = useState(true);
  const [activePlansError, setActivePlansError] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [plansPage, setPlansPage] = useState(1);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const itemsPerPage = 20;
  const API_URL = import.meta.env.VITE_URL_API;
  const { currencySymbol, exchangeRate, currency } = useCurrency();

  const formatPrice = (value) => {
    const base = Number(value || 0);
    if (!Number.isFinite(base)) return `${currencySymbol || currency || '€'} 0,00`;
    const rate = Number(exchangeRate) || 1;
    const converted = Math.round((base * rate + Number.EPSILON) * 100) / 100;
    return `${currencySymbol || currency || '€'} ${converted.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2000);
  }

  async function handleCancelPlan(plan) {
    try {
      const token = localStorage.getItem('auth_token');
      
      // Verifica se há imóveis vinculados ao plano
      if (plan.hasActiveProperties && plan.activeProperties.length > 0) {
        const propertyNames = plan.activeProperties.map(p => p.tituloIMOVEL).join(', ');
        showToast(`Não é possível cancelar o plano. Você possui ${plan.activeProperties.length} imóvel(is) vinculado(s): ${propertyNames}`, 'error');
        return;
      }
      
      // Cancela o plano usando PATCH com ativoCHECKOUT = 5 (cancelado)
      const resp = await fetch(`${API_URL}/checkouts/${plan.id}`, {
        method: 'PATCH',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ativoCHECKOUT: 5 })
      });
      
      if (!resp.ok) throw new Error('Falha ao cancelar plano');

      // Atualiza o status do plano para cancelado
      setActivePlans(prev => prev.map(p => p.id === plan.id ? { ...p, status: 'cancelled' } : p));
      showToast('Plano cancelado com sucesso.', 'success');
    } catch (e) {
      console.error(e);
      showToast('Não foi possível cancelar o plano.', 'error');
    }
  }


  useEffect(() => {
    const fetchCheckouts = async () => {
      setActivePlansLoading(true);
      setActivePlansError(null);
      try {
        const token = localStorage.getItem('auth_token');
        const [checkoutsResp, faturasResp] = await Promise.all([
          fetch(`${API_URL}/checkouts/agrupados`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          fetch(`${API_URL}/faturas`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          })
        ]);
        if (!checkoutsResp.ok) throw new Error(`Erro ao buscar pacotes (${checkoutsResp.status})`);
        const checkoutsData = await checkoutsResp.json();
        const faturasDataRaw = await faturasResp.json().catch(() => []);

        const rawCheckouts = checkoutsData?.checkouts || checkoutsData || [];
        const faturasData = Array.isArray(faturasDataRaw) ? faturasDataRaw : (faturasDataRaw.faturas || faturasDataRaw || []);

        // Mapear faturas por checkout
        const faturasByCheckout = {};
        faturasData.forEach(f => {
          const checkoutId = f?.checkout?.codigoCHECKOUT ?? f?.checkoutFATURA;
          if (checkoutId != null) {
            faturasByCheckout[String(checkoutId)] = f;
          }
        });

        // Adicionar checkouts que estão nas faturas mas não em /checkouts/agrupados (workaround)
        const checkoutMap = new Map();
        rawCheckouts.forEach(c => {
          const id = c?.codigoCHECKOUT;
          if (id != null) checkoutMap.set(String(id), c);
        });
        faturasData.forEach(f => {
          const checkout = f?.checkout;
          if (checkout && checkout.codigoCHECKOUT != null) {
            const id = String(checkout.codigoCHECKOUT);
            if (!checkoutMap.has(id)) {
              checkoutMap.set(id, checkout);
            }
          }
        });
        const mergedCheckouts = Array.from(checkoutMap.values()).map(c => ({
          ...c,
          _fatura: faturasByCheckout[String(c.codigoCHECKOUT)]
        }));

        const now = new Date();
        now.setHours(0, 0, 0, 0); // Zera as horas para comparar apenas a data

        const plans = mergedCheckouts.map((item) => {
          const dataAquisicao = new Date(item._fatura?.dataFATURA || item.dataCHECKOUT);

          const validade = new Date(dataAquisicao);
          const diasPeriodo = Number(item.periodoCHECKOUT) || 30;

          validade.setDate(validade.getDate() + diasPeriodo);
          validade.setHours(23, 59, 59, 999);

          // Verifica o status usando a mesma lógica do MyPackage
          let status;
          if (item.ativoCHECKOUT === 5) {
            status = 'cancelled';
          } else if (item.ativoCHECKOUT === 1) {
            // Se está ativo, verifica se expirou
            if (validade <= now) {
              status = 'expired';
            } else {
              status = 'active';
            }
          } else {
            // Se não está ativo (ativoCHECKOUT !== 1 e !== 5)
            if (validade <= now) {
              status = 'expired';
            } else {
              status = 'pending';
            }
          }

          // Calcula anúncios e destaques utilizados
          const adsUsed = item.quantidadeImoveis || 0;
          const adsTotal = Number(item.quantidadeCHECKOUT) || 0;
          const highlightsUsed = item.destaquesUsados || 0;
          const highlightsTotal = Number(item.plano?.quantidadePLANO || item.quantidadeCHECKOUT) || 0; // Para planos de destaque, usar quantidadePLANO
          
          // Verifica se tem imóveis ou destaques vinculados
          const hasActiveProperties = (item.imoveis && item.imoveis.length > 0) || (item.destaques && item.destaques.length > 0);
          const activeProperties = [...(item.imoveis || []), ...(item.destaques || [])];
          
          // Identifica o tipo de plano pela tipoPLANO
          const planType = item.plano?.tipoPLANO;
          const isHighlightPlan = planType === 5; // Tipo 5 = Destaque
          const isPropertyPlan = planType === 2 || planType === 3; // Tipo 2/3 = Imóveis

          // Identifica o tipo de plano (Férias, Particular, Profissional) pelo tipoPLANO
          let planTypeLabel = '';
          if (planType === 2) {
            planTypeLabel = 'Particular';
          } else if (planType === 3) {
            planTypeLabel = 'Profissional';
          } else if (planType === 4) {
            planTypeLabel = 'Férias';
          } else if (planType === 5) {
            planTypeLabel = 'Destaque';
          }

          return {
            id: item.codigoCHECKOUT,
            title: item.nomeCHECKOUT || item.plano?.nomePLANO || 'Pacote',
            description: planTypeLabel || item.plano?.descricaoPLANO || 'Pacote',
            status,
            price: Number(item.valorCHECKOUT || item.plano?.valorPLANO || 0),
            startDate: dataAquisicao.toLocaleDateString('pt-BR'),
            startDateISO: dataAquisicao.toISOString(),
            endDate: validade.toLocaleDateString('pt-BR'),
            adsUsed,
            adsTotal,
            highlightsUsed,
            highlightsTotal,
            hasActiveProperties,
            activeProperties,
            isHighlightPlan,
            isPropertyPlan,
            planTypeLabel
          };
        });
        setActivePlans(plans);

        const history = (mergedCheckouts || []).map((item) => {
          // Identifica o tipo de plano pelo tipoPLANO
          const planType = item.plano?.tipoPLANO;
          let planTypeLabel = '';
          if (planType === 2) {
            planTypeLabel = 'Particular';
          } else if (planType === 3) {
            planTypeLabel = 'Profissional';
          } else if (planType === 4) {
            planTypeLabel = 'Férias';
          } else if (planType === 5) {
            planTypeLabel = 'Destaque';
          }

          const status = (() => {
            const raw = (item.statusCHECKOUT !== undefined ? item.statusCHECKOUT : item.status);
            if (raw === 1 || raw === 'paid' || raw === 'pago') return 'paid';
            if (raw === 0 || raw === 'pending' || raw === 'pendente') return 'pending';
            return item.ativoCHECKOUT === 1 ? 'paid' : 'pending';
          })();

          const faturaData = item._fatura?.dataFATURA;
          return {
            id: `#${item.codigoCHECKOUT}`,
            date: new Date(faturaData || item.dataCHECKOUT).toLocaleDateString('pt-BR'),
            dateISO: new Date(faturaData || item.dataCHECKOUT).toISOString(),
            description: (item.nomeCHECKOUT || item.plano?.nomePLANO || 'Pacote') + ' - Aquisição',
            amount: Number(item.precoCHECKOUT || item.plano?.valorPLANO || 0),
            status,
            paymentMethod: item.metodoCHECKOUT || item.metodoPagamento || '—',
            planTypeLabel
          };
        });
        setPaymentHistory(history);
      } catch (err) {
        console.error(err);
        setActivePlansError('Não foi possível carregar seus pacotes pagos.');
        setActivePlans([]);
        setPaymentHistory([]);
      } finally {
        setActivePlansLoading(false);
      }
    };
    fetchCheckouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid': return 'Pago';
      case 'pending': return 'Pendente';
      case 'failed': return 'Rejeitado';
      default: return status;
    }
  };

  const filteredPayments = paymentHistory.filter(p => {
    const periodOk = periodFilter === 'all' || true; 
    const statusOk = statusFilter === 'all' || p.status === statusFilter;
    return periodOk && statusOk;
  });

  const sortedActivePlans = (activePlans || []).slice().sort((a, b) => new Date(b.startDateISO || b.startDate) - new Date(a.startDateISO || a.startDate));
  const totalPlanPages = Math.max(1, Math.ceil(sortedActivePlans.length / itemsPerPage));
  const paginatedPlans = sortedActivePlans.slice((plansPage - 1) * itemsPerPage, plansPage * itemsPerPage);

  const sortedPayments = (filteredPayments || []).slice().sort((a, b) => new Date(b.dateISO || b.date) - new Date(a.dateISO || a.date));
  const totalPaymentPages = Math.max(1, Math.ceil(sortedPayments.length / itemsPerPage));
  const paginatedPayments = sortedPayments.slice((paymentsPage - 1) * itemsPerPage, paymentsPage * itemsPerPage);

  const renderPagination = ({ current, total, onPrev, onNext }) => {
    if (total <= 1) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '12px 0' }}>
        <Button className="secondary" onClick={onPrev} disabled={current === 1}>
          Anterior
        </Button>
        <span style={{ fontWeight: 600, color: '#34574A' }}>Página {current} de {total}</span>
        <Button className="secondary" onClick={onNext} disabled={current === total}>
          Próxima
        </Button>
      </div>
    );
  };

  return (
    <PageContainer>
      {toast && (
        <div style={{position:'fixed',top:24,right:24,zIndex:9999,background:toast.type==='error'?'#e74c3c':'#27ae60',color:'#fff',padding:'16px 32px',borderRadius:8,fontWeight:600,boxShadow:'0 2px 16px rgba(0,0,0,0.08)'}}>
          {toast.msg}
        </div>
      )}
      <Header>
        <h1>Meus Pagamentos</h1>
      </Header>

      <Grid>
        {activePlansLoading && (
          <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#888' }}>Carregando pacotes...</div>
        )}
        {activePlansError && !activePlansLoading && (
          <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#c62828' }}>{activePlansError}</div>
        )}
        {!activePlansLoading && !activePlansError && activePlans.length === 0 && (
          <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#888' }}>Você ainda não possui pacotes.</div>
        )}
        {!activePlansLoading && !activePlansError && paginatedPlans.map(plan => (
          <PlanCard key={plan.id}>
            <PlanHeader>
              <div className="title">
                <h2>{plan.title}</h2>
                <div className="subtitle">{plan.description}</div>
               {/*  <div style={{ fontSize: '0.75rem', color: '#BA824F', fontWeight: '500', marginTop: '4px' }}>
                  {plan.planTypeLabel}
                </div> */}
              </div>
              <span className={`status ${plan.status}`}>
                {plan.status === 'active' ? 'Ativo' :
                 plan.status === 'cancelled' ? 'Cancelado' :
                 plan.status === 'pending' ? 'Pendente' : 'Expirado'}
              </span>
            </PlanHeader>

            <PlanDetails>
              <div className="detail">
                <div className="label">Valor Mensal</div>
                <div className="value">{formatPrice(plan.price)}</div>
              </div>
              <div className="detail">
                <div className="label">Período</div>
                <div className="value">{plan.startDate} - {plan.endDate}</div>
              </div>
              <div className="detail">
                <div className="label">
                  {plan.isHighlightPlan ? 'Destaques Utilizados' : 'Anúncios Utilizados'}
                </div>
                <div className="value">
                  {plan.isHighlightPlan 
                    ? (plan.highlightsTotal > 0 ? `${plan.highlightsUsed}/${plan.highlightsTotal} destaques` : '—')
                    : (plan.adsTotal > 0 ? `${plan.adsUsed}/${plan.adsTotal} anúncios` : '—')}
                </div>
              </div>
              <div className="detail">
                <div className="label">Próxima Cobrança</div>
                <div className="value">{plan.endDate}</div>
              </div>
            </PlanDetails>

            <PlanActions>
              {plan.status === 'active' ? (
                <Button className="secondary" onClick={() => handleCancelPlan(plan)}>Cancelar Plano</Button>
              ) : null}
            </PlanActions>
          </PlanCard>
        ))}
      </Grid>

      {!activePlansLoading && !activePlansError && activePlans.length > 0 && renderPagination({
        current: plansPage,
        total: totalPlanPages,
        onPrev: () => setPlansPage(p => Math.max(1, p - 1)),
        onNext: () => setPlansPage(p => Math.min(totalPlanPages, p + 1))
      })}

      <PaymentsSection>
        <SectionHeader>
          <h2>Histórico de Pagamentos</h2>
        </SectionHeader>

        <FiltersContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <label style={{ fontWeight: 500, fontSize: 14, color: '#34574A' }}>Período</label>
            <FilterSelect
              value={periodFilter}
              onChange={(e) => { setPeriodFilter(e.target.value); setPaymentsPage(1); }}
            >
              <option value="all">Todos os períodos</option>
              <option value="30days">Últimos 30 dias</option>
              <option value="3months">Últimos 3 meses</option>
              <option value="6months">Últimos 6 meses</option>
            </FilterSelect>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <label style={{ fontWeight: 500, fontSize: 14, color: '#34574A' }}>Status</label>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPaymentsPage(1); }}
            >
              <option value="all">Todos os status</option>
              <option value="paid">Pagos</option>
              <option value="pending">Pendentes</option>
              <option value="failed">Rejeitados</option>
            </FilterSelect>
          </div>
        </FiltersContainer>

        <PaymentsTable>
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Data</th>
                <th>Descrição</th>
                <th>Tipo</th>
                <th>Valor</th>
                <th>Método</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPayments.map(payment => (
                <tr key={payment.id}>
                  <td>{payment.id}</td>
                  <td>{payment.date}</td>
                  <td>{payment.description}</td>
                  <td style={{ color: '#BA824F', fontWeight: '500', fontSize: '0.875rem' }}>{payment.planTypeLabel || '—'}</td>
                  <td>{formatPrice(payment.amount)}</td>
                  <td>{payment.paymentMethod}</td>
                  <td>
                    <PaymentStatus className={payment.status}>
                      {getStatusLabel(payment.status)}
                    </PaymentStatus>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </PaymentsTable>

        {filteredPayments.length > 0 && renderPagination({
          current: paymentsPage,
          total: totalPaymentPages,
          onPrev: () => setPaymentsPage(p => Math.max(1, p - 1)),
          onNext: () => setPaymentsPage(p => Math.min(totalPaymentPages, p + 1))
        })}
      </PaymentsSection>
    </PageContainer>
  );
};

export default Payments;